<!-- WorkCenterMonitor.vue - Refactored -->
<template>
  <v-container
    fluid
    grid-list-xl
    pt-1
  >
    <core-refresh-informer
      :time="refreshTime"
      @refresh="refreshRows(true)"
    />
    <core-filter />

    <v-layout
      column
      align-end
      mt-xs--75
      mt-sm--75
      mt-md--75
      mt-lg--75
      mt-xl--75
      class="work-centers-monitor-toolbar"
    >
      <v-flex
        pa-0
        mr-4
      >
        <v-radio-group
          v-model="displayMode"
          class="inline-switch"
          row
          @change="handleDisplayModeChange"
        >
          <okil-tooltip
            show
            bottom
            :hint="$t('Common.singleList')"
          >
            <v-radio
              value="all"
              off-icon="mdi-format-list-bulleted"
              on-icon="mdi-format-list-bulleted-square"
            />
          </okil-tooltip>
          <okil-tooltip
            show
            bottom
            :hint="$t('Common.bySites')"
          >
            <v-radio
              value="site"
              off-icon="mdi-domain"
              on-icon="mdi-domain"
            />
          </okil-tooltip>
        </v-radio-group>
      </v-flex>
    </v-layout>

    <template>
      <template v-if="isDesktopView">
        <sticky-table-header
          :headers="localizedHeaders"
          :headers-widths="headersWidths"
          :show="showStickyHeader"
          :updating="updating"
        />

        <v-card
          v-for="group in displayGroups"
          :key="group.key"
          class="mobile-margin work-centers-monitor-group-card"
        >
          <div
            v-if="group.title"
            class="work-centers-monitor-group-title"
          >
            {{ group.title }}
          </div>

          <v-data-table
            :headers="localizedHeaders"
            :items="group.items"
            :search="search"
            item-key="WorkСenter"
            :rows-per-page-items="rowsPerPageItems"
            :rows-per-page-text="$t('Common.rowsPerPage')"
            :loading="updating"
            disable-initial-sort
            hide-actions
            fixed-header
            :class="['elevation-1 work-centers-monitor', renderGroupBySite ? 'work-centers-monitor-grouped' : '']"
            :style="groupedDesktopTableStyles"
          >
            <template v-slot:headers="{ headers: tableHeaders }">
              <tr ref="headers-row">
                <th
                  v-for="(header, index) in tableHeaders"
                  :ref="`header-${index}`"
                  :key="header.name"
                  class="column text-xs-left header"
                >
                  {{ header.name }}
                </th>
              </tr>
              <tr
                v-if="updating"
                class="progress"
              >
                <td
                  class="progress"
                  :colspan="tableHeaders.length"
                >
                  <v-progress-linear
                    height="2"
                    color="green"
                    indeterminate
                  />
                </td>
              </tr>
            </template>

            <template v-slot:items="{ item }">
              <tr :class="getRowClass(item)">
                <work-center-cell
                  :item="item"
                  @get-om="handleGetOM"
                  @set-print-form="setPrintForm"
                />
                <order-status-cell
                  :item="item"
                  :width="300"
                />
                <overrun-cell :item="item" />
                <work-center-status-cell :item="item" />
                <tuning-cell :item="item" />
                <printing-cell :item="item" />
                <time-to-end-cell :item="item" />
                <paint-time-cell :item="item" />
              </tr>
            </template>

            <template v-slot:no-data>
              <div class="text-xs-center">
                <span>{{ isLoading ? waitMsg : noDataMsg }}</span>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </template>

      <template v-else>
        <sticky-table-header
          :headers="mobileHeaders"
          :headers-widths="mobileHeadersWidths"
          :show="showStickyHeader"
          :updating="updating"
        />

        <v-card
          v-for="group in displayGroups"
          :key="group.key"
          class="mobile-margin work-centers-monitor-group-card"
        >
          <div
            v-if="group.title"
            class="work-centers-monitor-group-title"
          >
            {{ group.title }}
          </div>

          <v-data-table
            :headers="mobileHeaders"
            :items="group.items"
            :search="search"
            item-key="WorkСenter"
            :loading="updating"
            disable-initial-sort
            hide-actions
            dense
            class="work-centers-monitor-mobile"
          >
            <template v-slot:headers="{ headers: tableHeaders }">
              <tr>
                <th
                  v-for="(header, index) in tableHeaders"
                  :ref="`header-${index}`"
                  :key="`mobile-header-${index}`"
                  class="column text-xs-left header"
                >
                  {{ header.name }}
                </th>
              </tr>
              <tr
                v-if="updating"
                class="progress"
              >
                <td
                  class="progress"
                  :colspan="tableHeaders.length"
                >
                  <v-progress-linear
                    height="2"
                    color="green"
                    indeterminate
                  />
                </td>
              </tr>
            </template>

            <template v-slot:items="{ item }">
              <tr
                :key="rowKey(item)"
                :class="['mobile-row', getRowClass(item)]"
                @click="toggleRow(item)"
              >
                <td class="mobile-cell">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">
                        <v-icon
                          v-if="mobileRowAttention(item)"
                          small
                          class="mobile-attention-icon"
                        >
                          mdi-alert-box-outline
                        </v-icon>
                        <span
                          v-else
                          class="mobile-attention-spacer"
                        />
                        <strong>{{ item.WorkСenter }}</strong>
                      </span>
                    </template>
                    {{ item.WorkCenterName }}
                  </v-tooltip>

                  <template v-if="showMobileOrderInfo(item)">
                    <template v-if="hasAvTimeToChangeRoller(item)">
                      | <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <span v-on="on">
                            {{ mobileAvTimeToChangeRoller(item) }}
                          </span>
                        </template>
                        {{ $t('Common.avTimeToChangeRollerSec') }}
                      </v-tooltip>
                    </template>

                    <div class="br" />

                    <v-menu offset-y>
                      <template v-slot:activator="{ on }">
                        <a
                          class="link"
                          v-on="on"
                        >
                          <okil-cut-cell
                            :text="mobileOrderText(item)"
                            :text-length="20"
                            :width="200"
                            :tooltip-activator="false"
                            :crop="item.Customer.length > 10"
                          />
                        </a>
                      </template>
                      <v-list>
                        <v-list-tile>
                          <v-flex xs12>
                            <okil-print-form-button
                              label="ОБЗ"
                              :request-params="mobilePrintFormParams(item)"
                              @set-print-form="setPrintForm"
                            />
                          </v-flex>
                        </v-list-tile>
                        <v-list-tile>
                          <v-flex xs12>
                            <v-btn
                              block
                              color="blue-grey lighten-2"
                              @click="handleGetOM(item.DocNum, item.DocDate)"
                            >
                              <v-icon>mdi-image-outline</v-icon>
                              {{ $t('Common.getOM') }}
                            </v-btn>
                          </v-flex>
                        </v-list-tile>
                        <v-list-tile v-if="mobileOrderCalcBtnAllowed()">
                          <v-flex xs12>
                            <okil-print-form-button
                              label="Расчет"
                              :request-params="mobileCalcPrintFormParams(item)"
                              @set-print-form="setPrintForm"
                            />
                          </v-flex>
                        </v-list-tile>
                      </v-list>
                    </v-menu>
                  </template>
                </td>
                <td :class="['mobile-cell', 'mobile-status-cell', orderStatusCellClass(item) ? 'order-attention' : '']">
                  <span
                    v-if="showOrderDuration(item)"
                    :class="orderStatusCellClass(item) ? 'detail-warn' : ''"
                  >
                    <okil-cut-cell
                      :text="mobileOrderStatus(item)"
                      :text-length="20"
                      :width="200"
                      :tooltip-activator="false"
                      :crop="mobileOrderStatus(item).length > 10"
                    />
                    <div class="br" />
                    <span class="no-wrap">
                      {{ $t('Common.duration2') }}: {{ mobileOrderDuration(item) }}
                    </span>
                  </span>
                  <span v-else>-</span>
                  <v-icon
                    small
                    class="mobile-expand-icon"
                  >
                    {{ isRowExpanded(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </v-icon>
                </td>
              </tr>
              <tr
                v-if="isRowExpanded(item)"
                :key="`${rowKey(item)}-details`"
                :class="['mobile-details-row', printingStatusClass(item)]"
              >
                <td
                  colspan="2"
                  :class="printingStatusClass(item)"
                >
                  <table class="mobile-details-table">
                    <tbody>
                      <tr
                        v-for="detail in mobileDetailRows(item)"
                        :key="detail.key"
                      >
                        <td class="detail-label">
                          {{ detail.label }}
                        </td>
                        <td :class="['detail-value', detail.hasAttention ? 'detail-attention' : '']">
                          <div
                            v-for="(line, index) in detail.valueLines"
                            :key="index"
                            :class="detail.warnLineIndexes && detail.warnLineIndexes.includes(index) ? 'detail-warn' : ''"
                          >
                            {{ line }}
                            <v-icon
                              v-if="detail.icon && index === 0"
                              :color="detail.icon.color"
                              class="detail-icon"
                            >
                              {{ detail.icon.name }}
                            </v-icon>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </template>

            <template v-slot:no-data>
              <div class="text-xs-center">
                <span>{{ isLoading ? waitMsg : noDataMsg }}</span>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </template>

      <okil-new-pdf-viewer
        v-if="printForm"
        :src="printForm.doc.pdf"
        :title="printForm.name"
        show-title
        init-fullscreen
        @close="printForm = undefined"
      />

      <okil-original-maket-viewer
        v-if="omViewerOpen"
        :oms="oms"
        :fullscreen="screenWidth < 500"
        @close-om-viewer="closeOMViewer"
      />
    </template>
  </v-container>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { formatDate, copyObject, formatNumber, timeFromMinutes, allowed, isEmpty } from '../utils/functions'
import { rowHelper } from '../utils/wcStatesRowHelper'
import { UPDATE_INTERVAL } from '../constants'
import StickyTableHeader from '../components/tables/StickyTableHeader.vue'
import WorkCenterCell from '../components/tables/cells/WorkCenterCell.vue'
import OverrunCell from '../components/tables/cells/OverrunCell.vue'
import WorkCenterStatusCell from '../components/tables/cells/WorkCenterStatusCell.vue'
import OrderStatusCell from '../components/tables/cells/OrderStatusCell.vue'
import TuningCell from '../components/tables/cells/TuningCell.vue'
import PrintingCell from '../components/tables/cells/PrintingCell.vue'
import TimeToEndCell from '../components/tables/cells/TimeToEndCell.vue'
import PaintTimeCell from '../components/tables/cells/PaintTimeCell.vue'
import Vue from 'vue'

const GROUP_BY_SITE_STORAGE_KEY = 'work-center-monitor.group-by-site'

export default {
  name: 'WorkCenterMonitor',

  components: {
    StickyTableHeader,
    WorkCenterCell,
    OverrunCell,
    WorkCenterStatusCell,
    OrderStatusCell,
    TuningCell,
    PrintingCell,
    TimeToEndCell,
    PaintTimeCell
  },

  mixins: [rowHelper],

  data () {
    return {
      ipcRenderer: undefined,
      search: '',
      waitMsg: this.$i18n.t('Common.waitPlease'),
      noDataMsg: this.$i18n.t('Common.noData'),
      headers: [
        { name: 'Common.workCenter' },
        { name: 'Common.orderState' },
        { name: 'Common.overrun' },
        { name: 'Common.wcState' },
        { name: 'Common.tuning' },
        { name: 'Common.printingMeters' },
        { name: 'Common.timeToEnd' },
        { name: 'Common.paint' }
      ],
      headersWidths: [55, 198, 140, 161, 129, 168, 223, 95],
      mobileHeadersWidths: [0, 0],
      printForm: undefined,
      oms: [],
      omViewerOpen: false,
      showStickyHeader: false,
      updating: false,
      updateTimer: null,
      expandedRows: {},
      groupBySite: false,
      renderGroupBySite: false
    }
  },

  computed: {
    ...mapGetters({
      isDesktop: 'IS_DESKTOP',
      isAuthenticated: 'auth/IS_AUTHENTICATED'
    }),

    ...mapState({
      rows: state => state.wcs.rows,
      isLoading: state => state.loading.inProgress,
      refreshTime: state => state.wcs.refreshTime,
      screenWidth: state => state.viewParam['screen-width']
    }),

    items () {
      return Array.isArray(this.rows.rows) ? this.rows.rows : []
    },

    localizedHeaders () {
      return this.headers.map(header => ({
        name: this.$i18n.t(header.name)
      }))
    },

    rowsPerPageItems () {
      return [10, 15, 30, { text: this.$t('Common.all'), value: -1 }]
    },

    groupedDesktopTableStyles () {
      if (!this.renderGroupBySite || !this.isDesktopView) {
        return {}
      }

      return this.headersWidths.reduce((styles, width, index) => {
        styles[`--grouped-col-${index + 1}-width`] = `${width}px`
        return styles
      }, {})
    },

    isDesktopView () {
      return this.screenWidth >= 768
    },

    mobileHeaders () {
      if (this.localizedHeaders.length < 2) {
        return [{ name: this.$t('Common.workCenter') }, { name: this.$t('Common.orderState') }]
      }

      return [this.localizedHeaders[0], this.localizedHeaders[1]]
    },

    displayMode: {
      get () {
        return this.groupBySite ? 'site' : 'all'
      },
      set (value) {
        this.groupBySite = value === 'site'
      }
    },

    displayGroups () {
      if (!this.renderGroupBySite) {
        return [{
          key: 'all',
          title: '',
          items: this.items
        }]
      }

      const groups = []
      const itemsByGroup = {}

      this.items.forEach(item => {
        const groupKey = this.groupKey(item)

        if (itemsByGroup[groupKey] === undefined) {
          itemsByGroup[groupKey] = {
            key: `site-${groupKey}`,
            title: this.groupTitle(item),
            items: []
          }
          groups.push(itemsByGroup[groupKey])
        }

        itemsByGroup[groupKey].items.push(item)
      })

      if (groups.length === 0) {
        return [{
          key: 'all',
          title: '',
          items: []
        }]
      }

      return groups
    }
  },

  mounted () {
    this.initializeComponent()
  },

  beforeDestroy () {
    this.cleanupComponent()
  },

  methods: {
    async initializeComponent () {
      if (!this.isAuthenticated) {
        this.$router.push('/login')
        return
      }

      if (this.isDesktop) {
        this.ipcRenderer = window.require('electron').ipcRenderer
      }

      this.loadGroupBySitePreference()
      this.$root.$options.methods.setWidth()

      this.setupEventListeners()

      if (this.items.length === 0) {
        await this.refreshRows()
      } else {
        await this.syncRenderedDisplayMode()
        this.scheduleRefresh()
      }
    },

    cleanupComponent () {
      window.removeEventListener('scroll', this.handleScroll)
      window.removeEventListener('resize', this.handleResize)
      this.clearRefreshTimer()
    },

    setupEventListeners () {
      window.addEventListener('scroll', this.handleScroll)
      window.addEventListener('resize', this.handleResize)
    },

    clearRefreshTimer () {
      if (this.updateTimer) {
        clearTimeout(this.updateTimer)
        this.updateTimer = null
      }
    },

    scheduleRefresh () {
      this.clearRefreshTimer()
      this.updateTimer = setTimeout(() => {
        console.log('Штатное автообновление данных')
        this.updateTimer = null
        this.refreshRows(true)
      }, UPDATE_INTERVAL)
    },

    async refreshRows (silent = false) {
      this.updating = true
      try {
        const resp = await this.$store.dispatch('wcs/REQUEST_ROWS', silent)
        const data = resp.data
        if (!data.error) {
          await this.syncRenderedDisplayMode()
          this.sendKeepAlive()
        }
      } catch (error) {
        console.error('Ошибка при обновлении состояния:', error)
      } finally {
        this.updating = false
        this.scheduleRefresh()
      }
    },

    handleScroll () {
      this.showStickyHeader = scrollY > 110
    },

    async handleResize () {
      await this.syncRenderedDisplayMode()
    },

    updateStickyHeader () {
      this.$nextTick(() => {
        // Получаем реальные заголовки из таблицы
        const headers = copyObject(this.$refs, true)
          .filter(items => (items[0] !== undefined) && items[0].classList.contains('header'))
          .map(items => items[0])

        if (!headers.length) {
          return
        }

        const targetWidths = this.isDesktopView ? this.headersWidths : this.mobileHeadersWidths
        const headerCount = this.isDesktopView ? this.localizedHeaders.length : this.mobileHeaders.length

        // Обновляем массив ширин заголовков, который передается в компонент ликого заголовка,
        // где эти ширины будут применены
        headers.slice(0, headerCount).forEach((th, index) => {
          Vue.set(targetWidths, index, th.clientWidth)
        })
      })
    },

    sendKeepAlive () {
      if (!this.isDesktop || !this.ipcRenderer) {
        console.log('Отправка сигналов в Zabbix реализована только для настольной версии')
        return
      }

      const zabbixHost = process.env.VUE_APP_ZABBIX_HOST
      console.log(`Отправка keepalive-сигнала в Zabbix (${zabbixHost})...`)
      const result = this.ipcRenderer.sendSync('zabbix-send-keepalive', zabbixHost)
      console.log(result)
    },

    async handleGetOM (docNum, docDate) {
      try {
        const resp = await this.$store.dispatch('GET_OMS', {
          OrderNum: docNum,
          OrderDate: formatDate(docDate, 'YYYYMMDDHHmmss')
        })

        this.oms = resp.data

        if (this.oms.length === 0) {
          this.$store.commit('SET_LOADING_STATE', {
            inProgress: true,
            errorMessage: this.$i18n.t('Common.noOM')
          })
        } else {
          this.omViewerOpen = true
        }
      } catch (error) {
        console.error('Ошибка при получении ОМ:', error)
      }
    },

    closeOMViewer () {
      this.omViewerOpen = false
    },

    setPrintForm (data) {
      this.printForm = data
    },

    loadGroupBySitePreference () {
      const savedValue = localStorage.getItem(GROUP_BY_SITE_STORAGE_KEY)

      if (savedValue !== null) {
        this.groupBySite = savedValue === 'site' || savedValue === 'true'
        this.renderGroupBySite = this.groupBySite
      }
    },

    async handleDisplayModeChange (value) {
      this.groupBySite = value === 'site'
      localStorage.setItem(GROUP_BY_SITE_STORAGE_KEY, value)
      await this.syncRenderedDisplayMode()
    },

    async syncRenderedDisplayMode () {
      if (!this.isDesktopView) {
        this.renderGroupBySite = this.groupBySite
        this.updateStickyHeader()
        return
      }

      if (!this.groupBySite) {
        this.renderGroupBySite = false
        this.updateStickyHeader()
        return
      }

      this.renderGroupBySite = false
      await this.$nextTick()
      this.updateStickyHeader()
      await this.$nextTick()
      this.renderGroupBySite = true
      await this.$nextTick()
    },

    groupKey (item) {
      const siteKey = this.siteKey(item)

      if (!this.isRoomSplitSite(item)) {
        return siteKey
      }

      return `${siteKey}::${this.roomKey(item)}`
    },

    groupTitle (item) {
      const siteTitle = this.siteTitle(item)

      if (!this.isRoomSplitSite(item)) {
        return siteTitle
      }

      return `${siteTitle} - ${this.roomTitle(item)}`
    },

    isRoomSplitSite (item) {
      const siteName = (item.SiteName || '').trim()
      return siteName === 'Ликофлекс' || siteName === 'Common.likoflex'
    },

    siteKey (item) {
      return item.SiteName || '__without-site__'
    },

    siteTitle (item) {
      if (!item.SiteName) {
        return this.$t('Common.site')
      }

      return item.SiteName.startsWith('Common.')
        ? this.$t(item.SiteName)
        : item.SiteName
    },

    roomKey (item) {
      return item.Room || '__without-room__'
    },

    roomTitle (item) {
      return item.Room || '-'
    },

    getRowClass (item) {
      const timeToEnd = parseFloat(item.EstimatedTimeUntilTheEndPrint) || 0

      const status = this.orderStatus(item)

      // 'Работа с ведущим технологом / ожидание отв. лица'
      const pos1 = status.indexOf('ожидание')
      const pos2 = status.indexOf('отв')
      const pos3 = status.indexOf('лица')

      const classes = []

      if ((pos1 > -1) && (pos2 > pos1) && (pos3 > pos2)) {
        classes.push('blink-row')
      }

      if (this.isPrinting(item) && timeToEnd > 0 && timeToEnd <= 120) {
        classes.push('attention')
      }

      if (this.isTuning(item)) {
        classes.push('status-tuning')
      }

      if (this.isPrinting(item)) {
        classes.push('status-printing')
      }

      return classes.join(' ')
    },

    rowKey (item) {
      return item.WorkСenter || item.WorkCenter || item.Id || item.id
    },

    isRowExpanded (item) {
      return Boolean(this.expandedRows[this.rowKey(item)])
    },

    toggleRow (item) {
      const key = this.rowKey(item)
      if (!key) return

      Vue.set(this.expandedRows, key, !this.expandedRows[key])
    },

    showOrderDuration (item) {
      return !this.isEmptyStatus(item)
    },

    mobileOrderStatus (item) {
      if (this.isEmptyStatus(item)) return '-'
      return this.orderStatus(item)
    },

    mobileOrderDuration (item) {
      return timeFromMinutes(item.Duration)
    },

    showMobileOrderInfo (item) {
      return !this.isNoOrders(item) && !this.orderNotPresent(item)
    },

    mobileAvTimeToChangeRoller (item) {
      return timeFromMinutes(Math.round(parseInt(item.AvTimeToChangeRollerSec)))
    },

    mobileOrderText (item) {
      return `${item.Order}: ${item.Customer}`
    },

    mobilePrintFormParams (item) {
      return {
        DocType: 'ЗаказКлиента',
        PrintName: 'ВнутреннийБланкЗаказаЕРП',
        DocNum: item.DocNum,
        DocDate: formatDate(item.DocDate, 'YYYYMMDDHHmmss')
      }
    },

    mobileCalcPrintFormParams (item) {
      return {
        DocType: 'ЗаказКлиента',
        PrintName: 'РасчетЗаказа',
        DocNum: item.DocNum,
        DocDate: formatDate(item.DocDate, 'YYYYMMDDHHmmss')
      }
    },

    mobileOrderCalcBtnAllowed () {
      return allowed('button', 'order-calculation')
    },

    mobileRowAttention (item) {
      if (this.isEmptyStatus(item)) return false
      if (this.hasOverrunPrint(item)) return true
      if (this.hasTuningMetersWarning(item) || this.hasTuningTimeWarning(item)) return true
      if (this.hasOverrunMetersWarning(item) || this.hasOverrunTimeWarning(item)) return true
      if (!isEmpty(this.wcStatusCellClass(item))) return true
      if (!isEmpty(this.orderStatusCellClass(item))) return true
      if (!isEmpty(this.timeToEndCellClass(item))) return true
      if (!isEmpty(this.paintTimeCellClass(item))) return true

      return false
    },

    mobileDetailRows (item) {
      return [
        {
          key: 'overrun',
          label: this.$t('Common.overrun'),
          valueLines: this.mobileOverrunLines(item),
          warnLineIndexes: this.mobileOverrunWarnLines(item),
          hasAttention: this.mobileOverrunHasAttention(item)
        },
        {
          key: 'wcState',
          label: this.$t('Common.wcState'),
          valueLines: this.mobileWcStatusLines(item),
          warnLineIndexes: this.mobileWcStatusWarnLines(item),
          hasAttention: this.mobileWcStatusHasAttention(item)
        },
        {
          key: 'tuning',
          label: this.$t('Common.tuning'),
          valueLines: this.mobileTuningLines(item),
          warnLineIndexes: this.mobileTuningWarnLines(item),
          hasAttention: this.mobileTuningHasAttention(item)
        },
        {
          key: 'printing',
          label: this.$t('Common.printingMeters'),
          valueLines: this.mobilePrintingLines(item),
          warnLineIndexes: this.mobilePrintingWarnLines(item),
          hasAttention: this.mobilePrintingHasAttention(item)
        },
        {
          key: 'timeToEnd',
          label: this.$t('Common.timeToEnd'),
          valueLines: this.mobileTimeToEndLines(item),
          warnLineIndexes: this.mobileTimeToEndWarnLines(item),
          hasAttention: this.mobileTimeToEndHasAttention(item)
        },
        {
          key: 'paint',
          label: this.$t('Common.paint'),
          valueLines: this.mobilePaintLines(item),
          warnLineIndexes: this.mobilePaintWarnLines(item),
          hasAttention: this.mobilePaintHasAttention(item),
          icon: this.mobilePaintIcon(item)
        }
      ]
    },

    mobileOverrunLines (item) {
      if (this.isNoOrders(item) || this.orderNotPresent(item)) {
        return ['-']
      }

      const meters = formatNumber(this.overrunMeters(item), 2)
      const time = timeFromMinutes(this.overrunTime(item))
      return [
        `${this.$t('Common.meters2')}: ${meters}`,
        `${this.$t('Common.time')}: ${time}`
      ]
    },

    mobileOverrunWarnLines (item) {
      if (this.isNoOrders(item) || this.orderNotPresent(item)) {
        return []
      }

      const warnLines = []
      if (this.hasOverrunMetersWarning(item)) warnLines.push(0)
      if (this.hasOverrunTimeWarning(item)) warnLines.push(1)
      return warnLines
    },

    mobileOverrunHasAttention (item) {
      return this.hasOverrunMetersWarning(item) || this.hasOverrunTimeWarning(item)
    },

    mobileWcStatusLines (item) {
      if (this.isTuning(item)) {
        return [item.Status]
      }

      if (this.isPrinting(item)) {
        return [
          item.Status,
          `${this.$t('Common.lmpm')}: ${formatNumber(item.SpeedPlan)} / ${formatNumber(item.SpeedFact)}`
        ]
      }

      return ['-']
    },

    mobileWcStatusWarnLines (item) {
      if (this.isPrinting(item) && this.hasSpeedWarning(item)) {
        return [0, 1]
      }

      return []
    },

    mobileWcStatusHasAttention (item) {
      return this.wcStatusCellClass(item) !== ''
    },

    mobileTuningLines (item) {
      if (this.isEmptyStatus(item) || this.isNoOrders(item) || this.orderNotPresent(item)) {
        return ['-']
      }

      const metersPlan = formatNumber(item.MetersOnSettingPlan, 2)
      const metersFact = formatNumber(item.MetersOnSettingFact, 2)
      const timePlan = timeFromMinutes(item.TimeSettingPlan)
      const timeFact = timeFromMinutes(item.TimeSettingFact)

      return [
        `${this.$t('Common.meters2')}: ${metersPlan} / ${metersFact}`,
        `${this.$t('Common.time')}: ${timePlan} / ${timeFact}`
      ]
    },

    mobileTuningWarnLines (item) {
      if (this.isEmptyStatus(item) || this.isNoOrders(item) || this.orderNotPresent(item)) {
        return []
      }

      const warnLines = []
      if (this.hasTuningMetersWarning(item)) warnLines.push(0)
      if (this.hasTuningTimeWarning(item)) warnLines.push(1)
      return warnLines
    },

    mobileTuningHasAttention (item) {
      return this.hasTuningMetersWarning(item) || this.hasTuningTimeWarning(item)
    },

    mobilePrintingLines (item) {
      if (this.isEmptyStatus(item) || !this.isPrinting(item)) {
        return ['-']
      }

      const byOrder = formatNumber(item.MetersByOrder, true)
      const clean = formatNumber(item.PrintMetersClean, true)
      const overrunPercent = formatNumber(item.OverrunPrint)
      return [`${byOrder} / ${clean}`, `${overrunPercent}%`]
    },

    mobilePrintingWarnLines (item) {
      return (this.isPrinting(item) && this.hasOverrunPrint(item)) ? [0, 1] : []
    },

    mobilePrintingHasAttention (item) {
      return this.isPrinting(item) && this.hasOverrunPrint(item)
    },

    mobileTimeToEndLines (item) {
      if (this.isEmptyStatus(item) || !this.isPrinting(item)) {
        return ['-']
      }

      const byPrinting = timeFromMinutes(item.EstimatedTimeUntilTheEndPrint)
      const byNorm = timeFromMinutes(item.StandardTimeUntilPrinting)
      return [
        `${this.$t('Common.byPrinting')}: ${byPrinting}`,
        `${this.$t('Common.byNorm')}: ${byNorm}`
      ]
    },

    mobileTimeToEndWarnLines (item) {
      return this.isPrinting(item) && this.timeToEndCellClass(item) ? [0, 1] : []
    },

    mobileTimeToEndHasAttention (item) {
      return this.isPrinting(item) && this.timeToEndCellClass(item)
    },

    mobilePaintLines (item) {
      if (!this.isShowPaintTimeCell(item)) {
        return ['-']
      }

      return [timeFromMinutes(item.PaintTime)]
    },

    mobilePaintWarnLines (item) {
      return this.mobilePaintHasAttention(item) ? [0] : []
    },

    mobilePaintHasAttention (item) {
      return this.paintTimeCellClass(item)
    },

    mobilePaintIcon (item) {
      if (!this.isShowPaintTimeCell(item)) {
        return null
      }

      return {
        name: 'mdi-format-color-fill',
        color: this.hasPaint318Missing(item) ? 'orange darken-4' : 'green'
      }
    },

    printingStatusClass (item) {
      return this.isPrinting(item)
        ? 'status-printing'
        : (this.isTuning(item) ? 'status-tuning' : '')
    }
  }
}
</script>

<style lang="scss">

.work-centers-monitor-toolbar {
  margin-top: -16px;
  margin-bottom: 8px;
}

.work-centers-monitor-group-card + .work-centers-monitor-group-card {
  margin-top: 16px;
}

@media all and (max-width: 500px) {
  .work-centers-monitor-group-card {
    margin: 0 -12px 16px !important;
  }

  .work-centers-monitor-group-card:last-of-type {
    margin-bottom: 0 !important;
  }
}

.work-centers-monitor-group-title {
  padding: 8px 16px;
  font-size: 18px;
  font-weight: 600;
}

.inline-switch {
//  margin-right: 8px;
}

.switch-prepend {
  margin-right: -3px;
}

.switch-append {
  margin-left: -10px;
}

.work-centers-monitor {
  th {
    padding: 4px 8px !important;
    background-color: rgb(241, 241, 241);
  }

  td {
    padding: 4px 8px !important;
  }

  .theme--light.v-table tbody tr:not(:last-child) td,
  .theme--light.v-table thead tr:first-child th {
    border-bottom: 1px solid #a6a6a6 !important;
  }

  tr.attention {
    background-color: #FFCC80;
  }

  /* Определяем анимацию мигания */
  @keyframes blink {
    0%, 100% {
      background-color: transparent; /* или начальный цвет */
    }
    50% {
      background-color: #FFA473; /* цвет мигания */
    }
  }

  /* Применяем анимацию к строке */
  tr.blink-row {
    animation: blink 2s infinite; /* 1 секунда — длительность цикла, infinite — бесконечно */
  }

  td.attention {
    border-left: solid #ff6e20 5px;

    span.warn {
      font-weight: 500;
    }
  }

  td.attention:first-child {
    border-left: none;
  }

  .progress {
    padding: 0 !important;
    height: 2px;
  }

  .v-progress-linear {
    margin-top: -1px !important;
    margin-bottom: 0 !important;
  }

  .no-wrap {
    white-space: nowrap;
  }
}

.work-centers-monitor-grouped {
  th:nth-child(1),
  td:nth-child(1) {
    width: var(--grouped-col-1-width);
    min-width: var(--grouped-col-1-width);
  }

  th:nth-child(2),
  td:nth-child(2) {
    width: var(--grouped-col-2-width);
    min-width: var(--grouped-col-2-width);
  }

  th:nth-child(3),
  td:nth-child(3) {
    width: var(--grouped-col-3-width);
    min-width: var(--grouped-col-3-width);
  }

  th:nth-child(4),
  td:nth-child(4) {
    width: var(--grouped-col-4-width);
    min-width: var(--grouped-col-4-width);
  }

  th:nth-child(5),
  td:nth-child(5) {
    width: var(--grouped-col-5-width);
    min-width: var(--grouped-col-5-width);
  }

  th:nth-child(6),
  td:nth-child(6) {
    width: var(--grouped-col-6-width);
    min-width: var(--grouped-col-6-width);
  }

  th:nth-child(7),
  td:nth-child(7) {
    width: var(--grouped-col-7-width);
    min-width: var(--grouped-col-7-width);
  }

  th:nth-child(8),
  td:nth-child(8) {
    width: var(--grouped-col-8-width);
    min-width: var(--grouped-col-8-width);
  }
}

.work-centers-monitor .theme--light.v-table tbody tr.status-tuning td:first-child {
  box-shadow: inset 5px 0 0 #EF9A9A; /* red lighten-3 */
}

.work-centers-monitor .theme--light.v-table tbody tr.status-printing td:first-child {
  box-shadow: inset 5px 0 0 #90CAF9; /* blue lighten-3 */
}

.work-centers-monitor-mobile tbody tr.status-tuning:not(.mobile-details-row) td:first-child {
  box-shadow: inset 5px 0 0 #EF9A9A; /* red lighten-3 */
}

.work-centers-monitor-mobile tbody tr.status-printing:not(.mobile-details-row) td:first-child {
  box-shadow: inset 5px 0 0 #90CAF9; /* blue lighten-3 */
}

.work-centers-monitor-mobile tbody tr.mobile-details-row > td {
  position: relative;
  padding-left: 5px !important;
}

.work-centers-monitor-mobile tbody tr.mobile-details-row.status-tuning > td::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 5px;
  background: #EF9A9A; /* red lighten-3 */
}

.work-centers-monitor-mobile tbody tr.mobile-details-row.status-printing > td::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 5px;
  background: #90CAF9; /* blue lighten-3 */
}

.work-centers-monitor-mobile {
  --mobile-first-col-width: 50%;

  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }

  th,
  td {
    padding: 6px 8px !important;
    border-bottom: 1px solid #e0e0e0;
    vertical-align: top;
  }

  th {
    background-color: rgb(241, 241, 241);
  }

  .progress {
    padding: 0 !important;
    height: 2px;
  }

  .v-progress-linear {
    margin-top: -1px !important;
    margin-bottom: 0 !important;
  }

  th:first-child,
  td:first-child {
    width: var(--mobile-first-col-width);
  }

  th:nth-child(2),
  td:nth-child(2) {
    width: calc(100% - var(--mobile-first-col-width));
  }

  .mobile-row {
    cursor: pointer;
  }

  .mobile-status-cell {
    position: relative;
    padding-right: 26px !important;
  }

  .mobile-expand-icon {
    position: absolute;
    right: 6px;
    top: 6px;
    color: #8a8a8a;
  }

  .mobile-attention-icon {
    margin-top: -3px;
    margin-right: 4px;
    color: #ff6e20;
    vertical-align: middle;
  }

  .mobile-attention-spacer {
    display: inline-block;
    width: 16px;
    margin-right: 4px;
    vertical-align: middle;
  }

  .mobile-subtext {
    font-size: 12px;
    color: #6a6a6a;
  }

  .br {
    height: 5px !important;
  }

  .link {
    cursor: pointer;
    text-decoration: none;
  }

  .no-wrap {
    white-space: nowrap;
  }

  .mobile-details-row {
    td {
      padding: 0 !important;
    }

    td.status-printing {
      padding: 0 !important;
      border-bottom: 2px solid #90CAF9; /* blue lighten-3 */
    }

    td.status-tuning {
      padding: 0 !important;
      border-bottom: 2px solid #EF9A9A; /* red lighten-3 */
    }

    .mobile-details-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;

      td {
        padding: 4px 8px !important;
        border-bottom: 1px solid #f0f0f0;
        vertical-align: top;
      }
    }
  }

  .detail-label {
    width: var(--mobile-first-col-width);
    padding: 3px 4px !important;
  }

  .detail-value {
    width: calc(100% - var(--mobile-first-col-width));
    padding: 3px 10px 3px 4px !important;
  }

  .detail-attention {
    border-left: solid #ff6e20 5px;
  }

  .detail-warn {
    font-weight: 500;
  }

  .order-attention {
    box-shadow: inset 5px 0 0 #ff6e20;
  }

  .detail-icon {
    margin-top: 2px;
    margin-left: 6px;
    vertical-align: middle;
  }

  tr.attention {
    background-color: #FFCC80;
  }

  tr.blink-row {
    animation: blink 2s infinite;
  }
}
</style>
